import { cfgBrand } from '@infinitro/config';
import { translate, Verbiage } from '@infinitro/content';
import { Api, isSuccessStatus, loggedIn, startUiTimer, Status } from '@infinitro/global';
import { nationalSignificantDigits } from '@infinitro/input';
import { get, writable } from 'svelte/store';
import { ActionType, ActionTypeEnum, AsyncGetPhoneCountryConfig, AsyncGetUserActionInfo, AsyncGetVerifiedPhone, ConnectConfirmPhone, ConnectStartPhone, DataFieldEnum } from './api/api';
import { log, stateError, stateFatalError } from './state';
export const isPhoneVerificationEnabled = () => true;
export const statePhoneVerificationEnabled = writable(true);
/** Last phoneId returned from connectStartPhone. */
export const statePhoneEntityId = writable(undefined);
function toPhoneNumberInt(countryCode, nationalNumber) {
    const digits = nationalSignificantDigits(countryCode, nationalNumber);
    if (!digits)
        return null;
    const value = Number(digits);
    if (!Number.isSafeInteger(value) || value <= 0)
        return null;
    return value;
}
export const handleRequestPhoneVerifyCode = async (countryCode, nationalNumber, options = {}) => {
    log(`library default handleRequestPhoneVerifyCode: +${countryCode} ${nationalNumber}`);
    const isLoggedIn = get(loggedIn);
    if (!isLoggedIn) {
        const errorMessage = translate(Verbiage.please_contact_support) ?? 'Please sign in to verify your phone';
        stateError.set(errorMessage);
        stateFatalError.set(true);
        return [false, Status.FAILED, errorMessage, undefined];
    }
    // Resend (OTP modal): phoneId only.
    // New/changed number: countryCode + phoneNumber only — never reuse a prior phoneId
    // (mixing both paths returns REQUIRED_PARAMS).
    const resendPhoneId = options.phoneId?.trim() || undefined;
    let codeNum;
    let phoneNumberInt;
    if (!resendPhoneId) {
        codeNum = Number(String(countryCode).replace(/\D/g, ''));
        const parsed = toPhoneNumberInt(countryCode, nationalNumber);
        if (!Number.isFinite(codeNum) || (codeNum ?? 0) <= 0 || parsed == null) {
            const errorMessage = translate(Verbiage.verify_mobile_number);
            stateError.set(errorMessage);
            return [false, Status.FAILED, errorMessage, undefined];
        }
        phoneNumberInt = parsed;
    }
    try {
        const result = await ConnectStartPhone({
            variables: {
                forBonus: options.forBonus ?? false,
                ...(options.actionType != null ? { actionType: options.actionType } : {}),
                ...(resendPhoneId
                    ? { phoneId: resendPhoneId }
                    : { countryCode: codeNum, phoneNumber: phoneNumberInt })
            },
            context: { api: Api.player }
        });
        const data = result?.data?.connectStartPhone;
        if (data?.status === Status.TOO_MANY_ATTEMPTS) {
            stateFatalError.set(true);
            const errorMessage = translate(Verbiage.too_many_attempts_please_try_again_later);
            stateError.set(errorMessage);
            return [false, Status.TOO_MANY_ATTEMPTS, errorMessage, undefined];
        }
        if (isSuccessStatus(data?.status)) {
            stateError.set(undefined);
            stateFatalError.set(false);
            const nextPhoneId = data?.phoneId ?? resendPhoneId;
            if (nextPhoneId)
                statePhoneEntityId.set(nextPhoneId);
            if (data?.timeRemaining) {
                startUiTimer(data.timeRemaining, data.timeRemaining);
            }
            else {
                startUiTimer();
            }
            return [true, data?.status, undefined, nextPhoneId];
        }
        const errorMessage = data?.status ?? 'unknown error';
        stateError.set(errorMessage);
        return [false, data?.status ?? Status.FAILED, errorMessage, undefined];
    }
    catch (e) {
        console.warn('error requesting phone verify code', e);
        const errorMessage = e?.message ??
            e?.toString() ??
            'unknown error requesting phone verification code';
        stateError.set(errorMessage);
        return [false, Status.FAILED, errorMessage, undefined];
    }
};
export const handleCheckPhoneVerifyCode = async (phoneId, otpCode, actionType) => {
    log(`library default handleCheckPhoneVerifyCode: ${phoneId} OTP: ${otpCode}`);
    const isLoggedIn = get(loggedIn);
    if (!isLoggedIn) {
        const errorMessage = translate(Verbiage.please_contact_support) ?? 'Please sign in to verify your phone';
        stateError.set(errorMessage);
        stateFatalError.set(true);
        return [false, Status.FAILED, errorMessage, undefined];
    }
    if (!phoneId) {
        const errorMessage = translate(Verbiage.error);
        stateError.set(errorMessage);
        return [false, Status.FAILED, errorMessage, undefined];
    }
    try {
        const result = await ConnectConfirmPhone({
            variables: {
                phoneId,
                code: otpCode,
                ...(actionType != null ? { actionType } : {})
            },
            context: { api: Api.player }
        });
        const data = result?.data?.connectConfirmPhone;
        if (data?.status === Status.TOO_MANY_ATTEMPTS) {
            stateFatalError.set(true);
            const errorMessage = translate(Verbiage.too_many_attempts_please_try_again_later);
            stateError.set(errorMessage);
            return [false, Status.TOO_MANY_ATTEMPTS, errorMessage, undefined];
        }
        else if (data?.status === Status.FAILED) {
            const errorMessage = translate(Verbiage.incorrect_code) + ' ' + translate(Verbiage.please_try_again);
            stateError.set(errorMessage);
            return [false, Status.FAILED, errorMessage, undefined];
        }
        else if (data?.status === Status.CONFLICT) {
            const errorMessage = translate(Verbiage.please_contact_player_support);
            stateError.set(errorMessage);
            stateFatalError.set(true);
            return [true, Status.CONFLICT, errorMessage, undefined];
        }
        else if (isSuccessStatus(data?.status)) {
            stateFatalError.set(false);
            stateError.set(undefined);
            return [true, data?.status, undefined, data?.id ?? phoneId];
        }
        else if (data?.status) {
            const errorMessage = data?.status ?? 'unknown error';
            stateError.set(errorMessage);
            return [isSuccessStatus(data?.status), data?.status, errorMessage, undefined];
        }
        return [false, Status.FAILED, 'unknown error', undefined];
    }
    catch (e) {
        console.warn('error checking phone verify code', e);
        const errorMessage = e?.message ??
            e?.toString() ??
            'unknown error checking phone verification code';
        stateError.set(errorMessage);
        return [false, Status.FAILED, errorMessage, undefined];
    }
};
export async function loadPhoneCountryConfig(forBonus = true) {
    const result = await AsyncGetPhoneCountryConfig({
        variables: { brand: cfgBrand(), forBonus },
        context: { api: Api.player },
        fetchPolicy: 'network-only'
    });
    return result?.data?.getPhoneCountryConfig ?? [];
}
export async function loadVerifiedPhone() {
    const result = await AsyncGetVerifiedPhone({
        context: { api: Api.player },
        fetchPolicy: 'network-only'
    });
    return result?.data?.getVerifiedPhone ?? null;
}
function stripCountryCodeFromPhone(countryCode, phoneNumber) {
    const code = String(countryCode).replace(/\D/g, '');
    let digits = String(phoneNumber).replace(/\D/g, '');
    if (code && digits.startsWith(code)) {
        digits = digits.slice(code.length);
    }
    return nationalSignificantDigits(countryCode, digits) || digits;
}
/** Seeds dial code + national number from getVerifiedPhone when an unverified number exists. */
export function prefillFromVerifiedPhone(verified) {
    if (!verified?.countryCode || verified.phoneNumber == null || verified.isVerified) {
        return null;
    }
    return {
        dialCode: String(verified.countryCode),
        nationalNumber: stripCountryCodeFromPhone(verified.countryCode, verified.phoneNumber)
    };
}
export function prefillFromActionInfoFields(fields) {
    const countryField = fields.find((f) => f.dataField === DataFieldEnum.TEL_COUNTRY_CODE);
    const phoneField = fields.find((f) => f.dataField === DataFieldEnum.TEL_PHONE_NUMBER);
    if (!countryField?.dataValue?.trim() ||
        !phoneField?.dataValue?.trim() ||
        phoneField.dataVerified) {
        return null;
    }
    const dialCode = countryField.dataValue.replace(/\D/g, '');
    const nationalNumber = stripCountryCodeFromPhone(dialCode, phoneField.dataValue);
    if (!dialCode || !nationalNumber)
        return null;
    return { dialCode, nationalNumber };
}
async function loadPhonePrefillFromActionInfo(forBonus) {
    const actionTypes = forBonus
        ? [
            ActionTypeEnum.DEPOSIT,
            ActionTypeEnum.DEPOSIT_FIAT,
            ActionTypeEnum.DEPOSIT_CRYPTO,
            ActionTypeEnum.BONUS_CLAIM
        ]
        : [ActionTypeEnum.DEPOSIT, ActionTypeEnum.DEPOSIT_FIAT, ActionTypeEnum.DEPOSIT_CRYPTO];
    for (const actionType of actionTypes) {
        try {
            const result = await AsyncGetUserActionInfo({
                variables: { actionType },
                context: { api: Api.player },
                fetchPolicy: 'network-only'
            });
            const prefill = prefillFromActionInfoFields(result?.data?.getUserActionInfo ?? []);
            if (prefill)
                return prefill;
        }
        catch (e) {
            console.error('error loading phone prefill from action info', actionType, e);
        }
    }
    return null;
}
/** Resolves stored phone for AddPhone: getVerifiedPhone first, then deposit action-info fields. */
export async function loadPhonePrefill(forBonus = true) {
    const verified = await loadVerifiedPhone();
    if (verified?.isVerified) {
        return {
            prefill: null,
            alreadyVerified: true,
            verifiedKey: verified.phoneId ?? undefined
        };
    }
    const fromVerifiedPhone = prefillFromVerifiedPhone(verified);
    if (fromVerifiedPhone) {
        return { prefill: fromVerifiedPhone, alreadyVerified: false };
    }
    const fromActionInfo = await loadPhonePrefillFromActionInfo(forBonus);
    return { prefill: fromActionInfo, alreadyVerified: false };
}
/** @deprecated Prefer loadPhoneCountryConfig(true) */
export async function loadBonusPhoneCountryConfig() {
    return loadPhoneCountryConfig(true);
}
